import {ApplicationEntity} from "@/model/entity/application-entity"
import {FieldDescriptor, FieldTypeEnum} from "../field-descriptor"
import {countryList} from "@/model/service/country/country-service"
import {Country} from "../country/country"
import {Type} from "class-transformer"

export class ParcelShop extends ApplicationEntity {
  parcelShopGeneralInfo: ParcelShopGeneralInfo

  @Type(() => ParcelShopAddress)
  parcelShopAddress: ParcelShopAddress

  @Type(() => ParcelShopHours)
  parcelShopHours: ParcelShopHours

  parcelShopDowntime: ParcelShopDowntime

  parcelShopDowntimeLine: ParcelShopDowntimeLine

  parcelShopMgmtEntity: ParcelShopMgmtEntity

  @FieldDescriptor({fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel_shop.name", isFilterable: true})
  get name(): string {
    return this.parcelShopGeneralInfo.name
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.SELECT,
    fieldPrettyName: "entity.parcel_shop.country",
    selectionFieldDescription: {
      displayKey: "i18nKey",
      valueKey: "isoCodeAlpha2",
      values: countryList,
      i18n: true
    },
    isFilterable: true
  })
  @Type(() => Country)
  get country(): Country {
    return this.parcelShopAddress.country
  }

  @FieldDescriptor({fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel_shop.street", isFilterable: true})
  get street(): string {
    return this.parcelShopAddress.street
  }

  @FieldDescriptor({fieldType: FieldTypeEnum.TEXT, fieldPrettyName: "entity.parcel_shop.city", isFilterable: true})
  get city(): string {
    return this.parcelShopAddress.town
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel_shop.zip_code",
    isFilterable: true
  })
  get postalCode(): string {
    return this.parcelShopAddress.zipCode
  }

  constructor(
    parcelShopGeneralInfo: ParcelShopGeneralInfo,
    parcelShopAddress: ParcelShopAddress,
    parcelShopHours: ParcelShopHours,
    parcelShopDowntime: ParcelShopDowntime,
    parcelShopDowntimeLine: ParcelShopDowntimeLine,
    parcelShopMgmtEntity: ParcelShopMgmtEntity,
    id?: number
  ) {
    super(id)
    this.parcelShopGeneralInfo = parcelShopGeneralInfo
    this.parcelShopAddress = parcelShopAddress
    this.parcelShopHours = parcelShopHours
    this.parcelShopDowntime = parcelShopDowntime
    this.parcelShopDowntimeLine = parcelShopDowntimeLine
    this.parcelShopMgmtEntity = parcelShopMgmtEntity
  }

  mon1(): string {
    return this.workingTime(this.parcelShopHours.monH1OpenTm, this.parcelShopHours.monH2CloseTm)
  }

  mon2(): string {
    return this.workingTime(this.parcelShopHours.monH3OpenTm, this.parcelShopHours.monH4CloseTm)
  }

  tue1(): string {
    return this.workingTime(this.parcelShopHours.tueH1OpenTm, this.parcelShopHours.tueH2CloseTm)
  }

  tue2(): string {
    return this.workingTime(this.parcelShopHours.tueH3OpenTm, this.parcelShopHours.tueH4CloseTm)
  }

  wed1(): string {
    return this.workingTime(this.parcelShopHours.wedH1OpenTm, this.parcelShopHours.wedH2CloseTm)
  }

  wed2(): string {
    return this.workingTime(this.parcelShopHours.wedH3OpenTm, this.parcelShopHours.wedH4CloseTm)
  }

  thu1(): string {
    return this.workingTime(this.parcelShopHours.thuH1OpenTm, this.parcelShopHours.thuH2CloseTm)
  }

  thu2(): string {
    return this.workingTime(this.parcelShopHours.thuH3OpenTm, this.parcelShopHours.thuH4CloseTm)
  }

  fri1(): string {
    return this.workingTime(this.parcelShopHours.friH1OpenTm, this.parcelShopHours.friH2CloseTm)
  }

  fri2(): string {
    return this.workingTime(this.parcelShopHours.friH3OpenTm, this.parcelShopHours.friH4CloseTm)
  }

  sat1(): string {
    return this.workingTime(this.parcelShopHours.satH1OpenTm, this.parcelShopHours.satH2CloseTm)
  }

  sat2(): string {
    return this.workingTime(this.parcelShopHours.satH3OpenTm, this.parcelShopHours.satH4CloseTm)
  }

  sun1(): string {
    return this.workingTime(this.parcelShopHours.sunH1OpenTm, this.parcelShopHours.sunH2CloseTm)
  }

  sun2(): string {
    return this.workingTime(this.parcelShopHours.sunH3OpenTm, this.parcelShopHours.sunH4CloseTm)
  }

  workingTime(left: string, right: string): string {
    return this.range(left) + " - " +  this.range(right)
  }

  range(input: string): string {
    return input.substr(0, 2) + ":" + input.substr(2, 2)
  }


  workingDays(): any {
      return [
        {
          day: "entity.parcel_shop.week.mon",
          workTime1: this.mon1(),
          workTime2: this.mon2()
        }, {
          day: "entity.parcel_shop.week.tue",
          workTime1: this.tue1(),
          workTime2: this.tue2()
        }, {
          day: "entity.parcel_shop.week.wed",
          workTime1: this.wed1(),
          workTime2: this.wed2()
        }, {
          day: "entity.parcel_shop.week.thu",
          workTime1: this.thu1(),
          workTime2: this.thu2()
        }, {
          day: "entity.parcel_shop.week.fri",
          workTime1: this.fri1(),
          workTime2: this.fri2()
        }, {
          day: "entity.parcel_shop.week.sat",
          workTime1: this.sat1(),
          workTime2: this.sat2()
        }
        ]
  }

}

class ParcelShopGeneralInfo {
  pudoId: string
  carrierPudoId: string
  contactName: string
  name: string
  name2: string
  language: string
  maxparcel: number
  maxweight: number
  disableacc: number
  pkg: number
  type: number
  servicePudo: number
  complementaryInfo: string
  serviceComplementaryInfo: string
  carrierDepot: string
  driverTour: string

  constructor(
    pudoId: string,
    carrierPudoId: string,
    contactName: string,
    name: string,
    name2: string,
    language: string,
    maxparcel: number,
    maxweight: number,
    disableacc: number,
    pkg: number,
    type: number,
    servicePudo: number,
    complementaryInfo: string,
    serviceComplementaryInfo: string,
    carrierDepot: string,
    driverTour: string
  ) {
    this.pudoId = pudoId
    this.carrierPudoId = carrierPudoId
    this.contactName = contactName
    this.name = name
    this.name2 = name2
    this.language = language
    this.maxparcel = maxparcel
    this.maxweight = maxweight
    this.disableacc = disableacc
    this.pkg = pkg
    this.type = type
    this.servicePudo = servicePudo
    this.complementaryInfo = complementaryInfo
    this.pudoId = pudoId
    this.serviceComplementaryInfo = serviceComplementaryInfo
    this.carrierDepot = carrierDepot
    this.driverTour = driverTour
  }
}

class ParcelShopAddress {
  @Type(() => Country)
  addressCountry: Country
  street: string
  propertyNumber: string
  address2: string
  address3: string
  localizationHint: string
  floor: string
  building: string
  department: string
  @Type(() => Country)
  country: Country
  state: string
  zipCode: string
  suburbArea: string
  town: string
  description: string
  plat: string
  plong: string

  constructor(
    addressCountry: Country,
    street: string,
    propertyNumber: string,
    address2: string,
    address3: string,
    localizationHint: string,
    floor: string,
    building: string,
    department: string,
    country: Country,
    state: string,
    zipCode: string,
    suburbArea: string,
    town: string,
    description: string,
    plat: string,
    plong: string
  ) {
    this.addressCountry = addressCountry
    this.street = street
    this.propertyNumber = propertyNumber
    this.address2 = address2
    this.address3 = address3
    this.localizationHint = localizationHint
    this.floor = floor
    this.building = building
    this.department = department
    this.country = country
    this.state = state
    this.zipCode = zipCode
    this.suburbArea = suburbArea
    this.town = town
    this.description = description
    this.plat = plat
    this.plong = plong
  }
}

class ParcelShopHours {
  monH1OpenTm: string
  monH2CloseTm: string
  monH3OpenTm: string
  monH4CloseTm: string
  tueH1OpenTm: string
  tueH2CloseTm: string
  tueH3OpenTm: string
  tueH4CloseTm: string
  wedH1OpenTm: string
  wedH2CloseTm: string
  wedH3OpenTm: string
  wedH4CloseTm: string
  thuH1OpenTm: string
  thuH2CloseTm: string
  thuH3OpenTm: string
  thuH4CloseTm: string
  friH1OpenTm: string
  friH2CloseTm: string
  friH3OpenTm: string
  friH4CloseTm: string
  satH1OpenTm: string
  satH2CloseTm: string
  satH3OpenTm: string
  satH4CloseTm: string
  sunH1OpenTm: string
  sunH2CloseTm: string
  sunH3OpenTm: string
  sunH4CloseTm: string

  constructor(
    monH1OpenTm: string,
    monH2CloseTm: string,
    monH3OpenTm: string,
    monH4CloseTm: string,
    tueH1OpenTm: string,
    tueH2CloseTm: string,
    tueH3OpenTm: string,
    tueH4CloseTm: string,
    wedH1OpenTm: string,
    wedH2CloseTm: string,
    wedH3OpenTm: string,
    wedH4CloseTm: string,
    thuH1OpenTm: string,
    thuH2CloseTm: string,
    thuH3OpenTm: string,
    thuH4CloseTm: string,
    friH1OpenTm: string,
    friH2CloseTm: string,
    friH3OpenTm: string,
    friH4CloseTm: string,
    satH1OpenTm: string,
    satH2CloseTm: string,
    satH3OpenTm: string,
    satH4CloseTm: string,
    sunH1OpenTm: string,
    sunH2CloseTm: string,
    sunH3OpenTm: string,
    sunH4CloseTm: string
  ) {
    this.monH1OpenTm = monH1OpenTm
    this.monH2CloseTm = monH2CloseTm
    this.monH3OpenTm = monH3OpenTm
    this.monH4CloseTm = monH4CloseTm
    this.tueH1OpenTm = tueH1OpenTm
    this.tueH2CloseTm = tueH2CloseTm
    this.tueH3OpenTm = tueH3OpenTm
    this.tueH4CloseTm = tueH4CloseTm
    this.wedH1OpenTm = wedH1OpenTm
    this.wedH2CloseTm = wedH2CloseTm
    this.wedH3OpenTm = wedH3OpenTm
    this.wedH4CloseTm = wedH4CloseTm
    this.thuH1OpenTm = thuH1OpenTm
    this.thuH2CloseTm = thuH2CloseTm
    this.thuH3OpenTm = thuH3OpenTm
    this.thuH4CloseTm = thuH4CloseTm
    this.friH1OpenTm = friH1OpenTm
    this.friH2CloseTm = friH2CloseTm
    this.friH3OpenTm = friH3OpenTm
    this.friH4CloseTm = friH4CloseTm
    this.satH1OpenTm = satH1OpenTm
    this.satH2CloseTm = satH2CloseTm
    this.satH3OpenTm = satH3OpenTm
    this.satH4CloseTm = satH4CloseTm
    this.sunH1OpenTm = sunH1OpenTm
    this.sunH2CloseTm = sunH2CloseTm
    this.sunH3OpenTm = sunH3OpenTm
    this.sunH4CloseTm = sunH4CloseTm
  }

}

class ParcelShopDowntime {
  suggestionInd: string
  validFrom: string
  validTo: string
  lastDateDelivery: string
  firstDateDelivery: string

  constructor(
    suggestionInd: string,
    validFrom: string,
    validTo: string,
    lastDateDelivery: string,
    firstDateDelivery: string
  ) {
    this.suggestionInd = suggestionInd
    this.validFrom = validFrom
    this.validTo = validTo
    this.lastDateDelivery = lastDateDelivery
    this.firstDateDelivery = firstDateDelivery
  }
}

class ParcelShopDowntimeLine {
  startDt: string
  endDt: string
  indDt: string

  constructor(startDt: string, endDt: string, indDt: string) {
    this.startDt = startDt
    this.endDt = endDt
    this.indDt = indDt
  }
}

class ParcelShopMgmtEntity {
  billName: string
  billCompinfo: string
  billBusent: string
  billAsset: string
  billAssetcur: string
  billStreet: string
  billPropnum: string
  billAdd2: string
  billAdd3: string
  billFloor: string
  billBuilding: string
  billDepartement: string
  billCountrycode: string
  billState: string
  billZipcode: string
  billTown: string
  billVatrule: number
  billVatnum: string
  billContactname: string
  billContactMobile: string
  billContactPhone: string
  billContactEmail: string
  startMgmt: string
  billBankBic: string
  billBankIban: string
  billBankCode: string
  billBankAccountnumber: string
  billBankAccountholder: string
  partnerCode: string
  partnerLegalname: string
  billRules: string
  artnerPudoref: string
  keyaccount: number
  billPudocustid: string
  billPudodebtnum: string

  constructor(
    billName: string,
    billCompinfo: string,
    billBusent: string,
    billAsset: string,
    billAssetcur: string,
    billStreet: string,
    billPropnum: string,
    billAdd2: string,
    billAdd3: string,
    billFloor: string,
    billBuilding: string,
    billDepartement: string,
    billCountrycode: string,
    billState: string,
    billZipcode: string,
    billTown: string,
    billVatrule: number,
    billVatnum: string,
    billContactname: string,
    billContactMobile: string,
    billContactPhone: string,
    billContactEmail: string,
    startMgmt: string,
    billBankBic: string,
    billBankIban: string,
    billBankCode: string,
    billBankAccountnumber: string,
    billBankAccountholder: string,
    partnerCode: string,
    partnerLegalname: string,
    billRules: string,
    artnerPudoref: string,
    keyaccount: number,
    billPudocustid: string,
    billPudodebtnum: string
  ) {
    this.billName = billName
    this.billCompinfo = billCompinfo
    this.billBusent = billBusent
    this.billAsset = billAsset
    this.billAssetcur = billAssetcur
    this.billStreet = billStreet
    this.billPropnum = billPropnum
    this.billAdd2 = billAdd2
    this.billAdd3 = billAdd3
    this.billFloor = billFloor
    this.billBuilding = billBuilding
    this.billDepartement = billDepartement
    this.billCountrycode = billCountrycode
    this.billState = billState
    this.billZipcode = billZipcode
    this.billTown = billTown
    this.billVatrule = billVatrule
    this.billVatnum = billVatnum
    this.billContactname = billContactname
    this.billContactMobile = billContactMobile
    this.billContactPhone = billContactPhone
    this.billContactEmail = billContactEmail
    this.startMgmt = startMgmt
    this.billBankBic = billBankBic
    this.billBankIban = billBankIban
    this.billBankCode = billBankCode
    this.billBankAccountnumber = billBankAccountnumber
    this.billBankAccountholder = billBankAccountholder
    this.partnerCode = partnerCode
    this.partnerLegalname = partnerLegalname
    this.billRules = billRules
    this.artnerPudoref = artnerPudoref
    this.keyaccount = keyaccount
    this.billPudocustid = billPudocustid
    this.billPudodebtnum = billPudodebtnum
  }
}
